import { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useSyncExternalStore } from 'react';
import { ITableRowCount } from '@models/Forms/IForms';

interface ICountEntry {
    owner: string;
    count?: number;
    label?: string;
}

export const createTableCountStore = () => {
    const entries = new Map<string, ICountEntry>();
    const listeners = new Set<() => void>();
    let title: string | undefined;
    const notify = () => listeners.forEach((listener) => listener());
    return {
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        // Несколько явных привязок — неоднозначность: каждый счётчик остаётся локальным.
        getSnapshot: (): ICountEntry | undefined => (entries.size === 1 ? entries.values().next().value : undefined),
        set: (entry: ICountEntry) => {
            entries.set(entry.owner, entry);
            notify();
        },
        remove: (owner: string) => {
            entries.delete(owner);
            notify();
        },
        /** Заголовок блока — имя таблицы без собственного caption (шапка полноэкранного режима). */
        getTitle: () => title,
        setTitle: (next: string | undefined) => {
            if (next === title) return;
            title = next;
            notify();
        },
    };
};

interface ITableCountScope {
    blockId?: string;
    parent: ITableCountScope | null;
    store: ReturnType<typeof createTableCountStore>;
}

export const DocumentTableCountContext = createContext<ITableCountScope | null>(null);
const emptyStore = createTableCountStore();

export const useTableCountScope = (blockId?: string) => {
    const parent = useContext(DocumentTableCountContext);
    return useMemo<ITableCountScope>(() => ({ blockId, parent, store: createTableCountStore() }), [blockId, parent]);
};

/** Заголовок ближайшего блока формы; таблица вне блока получает undefined. */
export const useDocumentTableBlockTitle = () => {
    const store = useContext(DocumentTableCountContext)?.store ?? emptyStore;
    return useSyncExternalStore(store.subscribe, store.getTitle, store.getTitle);
};

/** Публикует только явно адресованный счётчик; не подписывает всю форму на число строк. */
export const useDocumentTableCount = (config: ITableRowCount | undefined, count: number | undefined) => {
    const owner = useId();
    let scope = useContext(DocumentTableCountContext);
    while (scope && scope.blockId !== config?.blockId) scope = scope.parent;
    const store = config?.enabled && config.blockId && scope ? scope.store : emptyStore;
    // Подписка на признак, а не на запись: запись новая на каждую смену числа строк, и таблица
    // перерисовывалась бы целиком вслед за собственной публикацией.
    const getBound = useCallback(() => store.getSnapshot()?.owner === owner, [store, owner]);
    const bound = useSyncExternalStore(store.subscribe, getBound, getBound);
    useLayoutEffect(() => {
        if (store === emptyStore) return;
        store.set({ owner, count, label: config?.label });
        return () => store.remove(owner);
    }, [store, owner, count, config?.label]);
    return bound;
};
